import { useMutation, useQuery } from '@apollo/client';
import { Button, notification, Steps } from 'antd';
import classnames from 'classnames';
import CircularProgress from 'components/CircularProgress';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import useWindowSize from 'hooks/useWindowSize';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router';
import { AuthAction } from 'redux/actions';
import ROUTES from 'routes/routeConstants';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GET_SINGLE_USER } from 'services/graphql/queries/user.query';
import { IUser, UserStatusEnum } from 'types/user';

import { getSteps } from './helpers';
import WizardHeaderLogo from './WizardHeaderLogo';

const { userSignOut } = AuthAction;

const { Step } = Steps;

const UserWizard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = React.useState(0);
  const { email, role, status, companyRole } = useCurrentLoggedInUser(); // test nha
  // const steps = getProfileTabs(role);
  const steps = getSteps(role, companyRole);
  const { isMobile } = useWindowSize();
  const { loading: isGetInformation, data, refetch } = useQuery<{ findOneUserEntity: IUser }>(GET_SINGLE_USER, {
    variables: {
      email,
    },
  });
  const intl = useIntl();
  const dispatch = useDispatch();

  const [updateUser] = useMutation(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_SINGLE_USER,
        variables: {
          email,
        },
      },
    ],
  });

  const history = useHistory();
  const userInfo = data?.findOneUserEntity;
  const isDone = useRef(false);

  useEffect(() => {
    if (status === UserStatusEnum.ACTIVE) {
      isDone.current = true;
    }
  }, [status]);

  const prev = () => {
    setCurrent(current - 1);
  };

  const onDone = async () => {
    setLoading(true);
    isDone.current = true;
    try {
      await updateUser({
        variables: {
          filter: {
            email,
          },
          updatedDoc: {
            status: UserStatusEnum.ACTIVE,
          },
        },
      });
      // dispatch(
      //   UserAction.fetchUserSuccess({
      //     status: UserStatusEnum.ACTIVE,
      //   }),
      // );
      setLoading(false);

      notification.success({
        message: 'Update profile successfully!',
        key: 'updateProfile',
        duration: 2,
      });

      history.push(ROUTES.DASHBOARD);
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Update failed! Please try again.',
        duration: 2,
        key: 'updateProfile',
      });
    }
  };

  // const createValidateEvent = (done: (data?: any) => void) => (_e: React.MouseEvent) => {
  //   const event = new CustomEvent('validate', {
  //     detail: {
  //       namespace: 'user-wizard',
  //       done,
  //       name: steps[current].titleId,
  //     },
  //   });

  //   window.dispatchEvent(event);
  //   // Save to array, where the events will be added a submit function to
  //   if (!events.current[current]) {
  //     events.current.push(event);
  //   }
  // };

  const onClickNext = () => {
    if (current !== steps.length - 1) setCurrent((c) => c + 1);
    else {
      onDone();
    }
  };

  // const onSubmit = async (_e: React.MouseEvent) => {
  //   const submit = events.current.map((e) => e.detail.submit).filter(Boolean);

  //   if (submit.length) {
  //     setLoading(true);

  //     // Submit function was added to the event when validate is called and success
  //     // Now we submit them
  //     await Promise.all(submit.map((fn) => fn()));
  //     await onDone();

  //     setLoading(false);
  //   }
  // };

  const saveInfoButtonLabel = current < steps.length - 1 ? 'Next' : 'Done';

  const onLogOutClick = useCallback(() => {
    dispatch(userSignOut());
  }, []);

  const footer = (
    <>
      {current > 0 && (
        <Button disabled={loading} style={{ margin: '0 20px' }} onClick={() => prev()}>
          Previous
        </Button>
      )}
      {current < steps.length - 1 &&
        // <Button type='primary' onClick={onClickNext}>
        //   Next
        // </Button>
        null}
      {current === steps.length - 1 &&
        // <Button loading={loading} type='primary' onClick={createValidateEvent(onSubmit)}>
        //   Done
        // </Button>
        null}
      <div className='wizard-save-button' />
    </>
  );

  const handleBlockedNavigation = () => {
    if (isDone.current) return true;

    const label = intl.formatMessage({
      id: 'setup.leave.warning',
      defaultMessage:
        'It looks like you have not done your information setup. If you leave before saving, your changes will be lost.',
    });

    return window.confirm(label);
  };

  // const handleStepClick = (index: number) => {
  //   if (!loading) {
  //     setCurrent(index);
  //   }
  // };

  const Components = useMemo(
    () =>
      steps.map((step, index: number) => {
        const Comp = step.component;
        const isCurrentActiveStep = index === current;
        if (index > current) return null; // Dont pre-render next components

        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className={`${isCurrentActiveStep ? 'active' : 'gx-d-none'} gx-py-4`}>
            {isGetInformation || !userInfo ? (
              <CircularProgress />
            ) : (
              // <Comp hideSaveButton={!isCurrentActiveStep} refetchUserData={refetch} user={userInfo} />
              <Comp
                refetch={refetch}
                isActive={isCurrentActiveStep}
                onClickNext={onClickNext}
                buttonLabel={saveInfoButtonLabel}
                userInfo={userInfo}
                nextLoading={loading}
                saveButtonContainerClass='.user-wizard-wrapper .wizard-save-button'
              />
            )}
          </div>
        );
      }),
    [loading, userInfo, current],
  );

  return (
    <div className='gx-main-content-wrapper gx-p-0 gx-h-100'>
      <Prompt message={() => handleBlockedNavigation()} />
      <div className={classnames(`user-wizard-wrapper gx-h-100`, { mobile: isMobile })}>
        <div className='wizard-container'>
          <WizardHeaderLogo />
          <div className='steps-content-wrapper'>
            <div className='steps-header'>
              <div className='step-title'>
                <FormattedMessage id={`userProfile.${steps[current]?.titleId}`} />
              </div>
              {steps.length > 1 && (
                <div style={{ marginTop: 15 }}>
                  <Steps current={current}>
                    {steps.map((item) => (
                      <Step
                        // onClick={() => handleStepClick(index)}
                        key={item.titleId}
                      />
                    ))}
                  </Steps>
                </div>
              )}
            </div>
            {/* <ContentHeader
              className='gx-justify-content-center gx-flex-column gx-align-items-center'
              titleId={steps[current]?.titleId}
              toggle={false}
            /> */}
            <div className={classnames('steps-content')}>
              <div className='gx-overflow-y-auto scrollbox form-container'>{Components}</div>
            </div>
            <div className='user-wizard-footer-container'>
              <div className='steps-action'>{footer}</div>
              <div className='gx-d-flex gx-justify-content-center'>
                <ButtonWithSpinner bgColor='transparent' buttonType='text' color='#038fde' onClick={onLogOutClick}>
                  Want to change to another account?
                </ButtonWithSpinner>
              </div>
            </div>
          </div>
        </div>
        <div className='wizard-copyright-footer'>
          <span>Copyright Notary &copy; 2019</span>
        </div>
      </div>
    </div>
  );
};

export default UserWizard;
